<template>
    <v-col cols="12" md="8" class="mx-auto my-12 primary-form">
        <v-form v-if="!submitted" key="contact-form" ref="form">
            <div ref="formContainer">
                <v-row justify="center" dense>
                    <section class="text-center">
                        <h2 class="secondary--text h3">
                            {{ fields.heading }}
                        </h2>
                        <p>{{ fields.subHeading }}</p>
                    </section>
                </v-row>
                <v-row dense>
                    <v-col cols="12" sm="6">
                        <v-label for="firstName"> First name * </v-label>
                        <base-input
                            v-model="model.firstName"
                            name="firstName"
                            color="primary"
                            autocomplete="given-name"
                            required
                        />
                    </v-col>
                    <v-col cols="12" sm="6">
                        <v-label for="lastName"> Last name * </v-label>
                        <base-input
                            v-model="model.lastName"
                            name="lastName"
                            color="primary"
                            autocomplete="family-name"
                            required
                        />
                    </v-col>
                    <v-col
                        cols="12"
                        v-if="model.type === FormTypes.agentTraining"
                    >
                        <v-label for="NPN">NPN#</v-label>
                        <base-input
                            v-model="model.npnNumber"
                            name="NPN"
                            color="primary"
                        />
                    </v-col>
                    <v-col cols="12">
                        <v-label for="email"> Email (optional)</v-label>
                        <base-input
                            v-model="model.email"
                            name="email"
                            color="primary"
                            autocomplete="email"
                            email
                        />
                    </v-col>
                    <v-col cols="12">
                        <v-label for="phone">
                            Phone number
                            {{
                                model.type !== FormTypes.agentTraining
                                    ? '(optional)'
                                    : ''
                            }}
                        </v-label>
                        <base-input
                            v-model="model.phone"
                            name="phone"
                            color="primary"
                            autocomplete="tel"
                            phone
                            :required="model.type === FormTypes.agentTraining"
                        />
                    </v-col>
                    <template v-if="model.type !== FormTypes.agentTraining">
                        <v-col cols="12">
                            <v-label for="address"> Street address * </v-label>
                            <base-input
                                v-model="model.address1"
                                name="address"
                                color="primary"
                                autocomplete="street-address"
                                :required="optionalFieldsRequired"
                            />
                        </v-col>
                        <v-col cols="12">
                            <v-label for="address2">
                                Apt, suite, bldg (optional)
                            </v-label>
                            <base-input
                                v-model="model.address2"
                                name="address2"
                                color="primary"
                            />
                        </v-col>
                    </template>
                </v-row>
                <template v-if="model.type !== FormTypes.agentTraining">
                    <v-row dense>
                        <v-col cols="6" md="4">
                            <v-label for="city"> City * </v-label>
                            <base-input
                                v-model="model.city"
                                name="city"
                                color="primary"
                                autocomplete="address-level2"
                                :required="optionalFieldsRequired"
                            />
                        </v-col>
                        <v-col cols="6" md="4">
                            <custom-select-new
                              ref="stateSelect"
                              name="state"
                              v-model="model.state"
                              display="State *"
                              item-text="name"
                              item-value="name"
                              @optionSelected="(selected) => setStateOnModel(selected)"
                              :items="states"
                              :required="optionalFieldsRequired"/>
                        </v-col>
                        <v-col cols="6" md="4">
                          <custom-select-new
                              ref="countySelect"
                              name="county"
                              v-model="model.county"
                              display="County *"
                              item-text="name"
                              item-value="name"
                              @input="checkState()"
                              @optionSelected="(selected) => setCountyOnModel(selected)"
                              :key="model.state"
                              :items="counties"
                              :required="optionalFieldsRequired"/>
                        </v-col>
                        <v-col cols="6" md="4">
                            <v-label for="zip"> Zip code * </v-label>
                            <base-input
                                v-model="model.zip"
                                name="zip"
                                color="primary"
                                autocomplete="postal-code"
                                :rules="zipCodeRules"
                                :required="optionalFieldsRequired"
                            />
                        </v-col>
                    </v-row>
                </template>
                <template v-if="model.type === FormTypes.agentTraining">
                    <ol>
                        <li>
                            Your request for NextBlue Medicare Advantage Product
                            Training will be sent to
                            <a
                                :href="
                                    'mailto:' + siteConfig.agentTrainingEmail
                                "
                            >
                                {{ siteConfig.agentTrainingEmail }}
                            </a>
                        </li>
                        <li>
                            The coordinators will review your information and
                            verify that you represent and sell NextBlue
                            products.
                        </li>
                        <li>
                            Upon their approval, you will receive an email with
                            a link to complete the NextBlue MA Product Training
                            from Compliance 360
                        </li>
                    </ol>
                </template>
                <small class="font-small required-field-text">
                    * indicates a required field <br>
                </small>
                <v-row v-if="siteConfig" justify="center" dense>
                    <v-col cols="12">
                        <small v-if="wrongState" class="font-small error--text ">
                            We're sorry, our guides are only available to be
                            mailed to addresses within {{ siteConfig.state }}. However, you are
                            still able to download a digital version of the
                            guide above. For any questions, please call {{ siteConfig.utilityNavContactPhone }}
                        </small>
                    </v-col>
                    <v-col cols="12">
                        <small id="agreement" class="font-small">
                            By providing your phone number and/or email, you
                            give permission for
                            {{ siteConfig.careNetworkName }} to contact you
                            about this communication, answer your questions, and
                            discuss your options.
                        </small>
                        <v-checkbox
                            v-model="model.followUp"
                            color="black"
                            :label="
                                'Yes, I\'d like to receive future communications from ' +
                                siteConfig.careNetworkName
                            "
                            name="followUp"
                            :ripple="false"
                            aria-describedby="agreement"
                            required
                        />
                    </v-col>
                </v-row>
                <v-row justify="center" dense>
                    <base-button
                        text="Submit"
                        type="submit"
                        :loading="loading"
                        @click="submit"
                    />
                </v-row>
                <p v-if="didError && siteConfig" class="error--text mt-4">
                    Something went wrong! Look's like your request could not be
                    completed. If this problem persists for you please reach out
                    to us directly at
                    {{ siteConfig.customerContactPhone | formatPhone }}
                </p>
            </div>
        </v-form>
        <confirmation-snippet
            v-if="submitted && !didError"
            key="confirmation"
            :style="{ height: formHeight + 'px' }"
            :message="getMessage()"
        />
    </v-col>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import ConfirmationSnippet from '../../snippets/confirmation-snippet'
import PixelTracker from '../../base/pixel-tracker'
import { FormTypes, NATIONS_TYPES } from '@/store/modules/nations'
import { states } from '../../../utils/states'
import { getCounties, stateCountyData as counties, defaultCounties } from '../../../utils/counties'
import CustomSelectNew from '../../base/custom-select-new.vue'

export default {
  name: 'ContactForm',
  components: {
    ConfirmationSnippet,
    CustomSelectNew
  },
  props: {
    fields: {
      type: Object,
      required: true
    }
  },
  data () {
    return {
      states,
      counties,
      wrongState: false,
      submitted: false,
      model: {
        type: this.fields.type,
        firstName: '',
        lastName: '',
        npnNumber: '',
        email: '',
        phone: '',
        address1: '',
        address2: '',
        city: '',
        state: '',
        prevState: 'n/a',
        county: '',
        zip: '',
        followUp: true
      },
      zipCodeRules: [
        (v) =>
          !v ||
                    /(^\d{5}$)|(^\d{9}$)|(^\d{5}-\d{4}$)]/.test(v) ||
                    'Invalid Zip Code'
      ],
      countyRules: [
        (v) =>
          !v ||
                    !/Select a State/.test(v) ||
                    'Invalid County'
      ],
      stateRules: [
        (v) =>
          !v ||
                    !/Select/.test(v) ||
                    'Invalid State'
      ],
      FormTypes: FormTypes
    }
  },
  computed: {
    ...mapState({ siteConfig: (state) => state.site.siteConfig }),
    ...mapState('nations', ['loading', 'didError']),
    formHeight () {
      return this.$refs.formContainer.clientHeight
    },
    optionalFieldsRequired () {
      const isRequired = this.fields.type === FormTypes.promotionalItem ||
        this.fields.type === FormTypes.contactRequest ||
        this.fields.type === FormTypes.commonSense
      return isRequired
    }
  },
  created () {
    if (this.fields.type === FormTypes.commonSense) {
      this.stateRules.push(v => (!v || this.siteConfig.state.toLowerCase() ===
                    v.toLowerCase()) || 'invalid state')
    }
    this.counties = defaultCounties[0].counties
  },
  methods: {
    ...mapActions({
      submitLead: NATIONS_TYPES.actions.submitLead
    }),
    setStateOnModel (selected) {
      this.model.state = selected
      this.checkState()
    },
    setCountyOnModel (selected) {
      this.model.county = selected
    },
    checkState () {
      if (
        this.model.state &&
        this.fields.type === FormTypes.commonSense &&
                this.siteConfig.state.toLowerCase() !==
                    this.model.state.toLowerCase()
      ) {
        this.wrongState = true
      } else {
        this.wrongState = false
      }
      this.updateCounties()
    },
    updateCounties () {
      this.counties = getCounties(this.model.state)
      // reset county field if state was changed
      if (this.model.state !== this.model.prevState) {
        this.model.prevState = this.model.state
        this.model.county = ''
      }
    },
    submit () {
      if (this.validate(this.$refs.form)) {
        this.$emit('submit', this.model)
        switch (this.fields.type) {
        case FormTypes.commonSense:
          this.siteConfig.careNetworkName.indexOf('Vermont') ===
                        -1
            ? PixelTracker.track(
              '601117795',
              'iIa7CPKT-9sBEOOo0Z4C'
            )
            : PixelTracker.track(
              '608189871',
              'VVNtCN2rqNkBEK_7gKIC'
            )
          break
        case FormTypes.promotionalItem:
          break
        case FormTypes.agentTraining:
          break
        default:
          this.siteConfig.careNetworkName.indexOf('Vermont') ===
                        -1
            ? PixelTracker.track(
              '601117795',
              'PVe_CL--6tsBEOOo0Z4C'
            )
            : PixelTracker.track(
              '608189871',
              'RL6QCP-pqNkBEK_7gKIC'
            )
          break
        }
        this.submitted = false
        this.submitLead(this.model).then(() => {
          if (this.didError) {
            if (this.$analytics) {
              this.$analytics.track(
                'Contact_Form_Submissions_failed'
              )
            }
            return
          }
          this.submitted = true
          if (this.$analytics) {
            this.$analytics.track('Contact_Form_Submissions')
          }
        })
      }
    },
    getMessage () {
      let msg = ''
      switch (this.fields.type) {
      case FormTypes.commonSense:
        msg = 'We’ll send your requested guide by mail shortly.'
        break
      case FormTypes.promotionalItem:
        msg = 'We’ll send your requested Health and Safety kit to the address provided.'
        break
      default:
        msg = 'Our team will be in touch with you shortly!'
        break
      }
      return msg
    },
    validate (form) {
      var customHasErrors = false
      const errs = form.inputs.filter(function (input) {
        return !input.validate(true)
      })
      if (this.optionalFieldsRequired) {
        this.$refs.stateSelect.validate()
        this.$refs.countySelect.validate()
        customHasErrors = !this.$refs.stateSelect.isValid() || !this.$refs.countySelect.isValid()
      }
      if (errs.length) {
        errs[0].focus()
        return false
      } if (customHasErrors) {
        if (!this.$refs.stateSelect.isValid()) {
          this.$refs.stateSelect.focus()
        } else {
          this.$refs.countySelect.focus()
        }
        return false
      } else {
        return true
      }
    }
  }
}
</script>

<style lang="scss" scoped>
@import '../../../styles/variables';

label {
    color: $dark-grey !important;
}
</style>
